/**
 * Sidebar — workspace-aware left sidebar for the coherent app shell (Option A).
 *
 * Generalises AdminNav's IA pattern to all workspace types:
 *   - personal   : Dashboard, Catalogue, My data, My activity
 *   - company    : Dashboard, Members, Assignments, Attempts, Share tokens
 *   - admin      : existing admin IA (Question bank … Audit log)
 *
 * Styling: Space Grotesk 14px/500 sentence-case links (the one approved
 * deviation from design.md Space Mono). Group headers stay Space Mono 11px
 * eyebrow. Classes from shell.css.
 *
 * No `any` (Inv 13). No console.log (Inv 12). All nav uses <Link> (Inv 14).
 */

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { BrandMark } from '../app-shell/BrandMark.tsx';
import { QuotaMeter } from './QuotaMeter.tsx';
import { apiFetch } from '@/lib/api-client';

interface NavItem {
  href: string;
  label: string;
  /** True if the item matches when currentPathname starts with href. Default: exact match. */
  startsWith?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

// ── Personal IA ──────────────────────────────────────────────────────────────
const PERSONAL_GROUPS: NavGroup[] = [
  {
    label: 'Personal',
    items: [
      { href: '/dashboard', label: 'Dashboard' },
      { href: '/catalogue', label: 'Catalogue', startsWith: true },
      { href: '/me/data', label: 'My data' },
      { href: '/me/activity', label: 'My activity' },
    ],
  },
];

// ── Company IA ───────────────────────────────────────────────────────────────
// Account-level pages (My data / My activity) are personal-workspace concerns
// and must not render inside a company workspace (Fix 1, 2026-05-30).
function companyGroups(companyId: string): NavGroup[] {
  return [
    {
      label: 'Company', // overridden at render time with company name via prop
      items: [
        { href: `/company/${companyId}/dashboard`, label: 'Dashboard' },
        { href: `/company/${companyId}/members`, label: 'Members', startsWith: true },
        { href: '/company/assignments', label: 'Assignments', startsWith: true },
        { href: `/company/${companyId}/attempts`, label: 'Attempts', startsWith: true },
        { href: `/company/${companyId}/share-tokens`, label: 'Share tokens', startsWith: true },
      ],
    },
  ];
}

// ── Admin IA ──────────────────────────────────────────────────────────────────
const ADMIN_GROUPS: NavGroup[] = [
  {
    label: 'System Admin',
    items: [
      { href: '/admin/questions', label: 'Question bank', startsWith: true },
      { href: '/admin/tests', label: 'Test composer', startsWith: true },
      { href: '/admin/companies', label: 'Companies', startsWith: true },
      { href: '/admin/attempts', label: 'All attempts' },
      { href: '/admin/users/erase', label: 'GDPR erasure' },
      { href: '/admin/users/extended-time-grant', label: 'Extended-time grant' },
      { href: '/admin/system-health', label: 'System health' },
      { href: '/admin/audit-log', label: 'Audit log' },
    ],
  },
];

export type SidebarWorkspace = 'personal' | 'company' | 'admin';

export interface SidebarProps {
  workspace: SidebarWorkspace;
  currentPathname: string;
  /** Company name — used as group label in company mode. */
  companyName?: string;
  /** Company ID — required in company mode. */
  companyId?: string;
  /** Whether the off-canvas drawer is open (mobile only). */
  drawerOpen?: boolean;
  /** Called when drawer should close (scrim click, nav item tap). */
  onDrawerClose?: () => void;
}

function isActive(item: NavItem, pathname: string): boolean {
  if (item.startsWith) return pathname.startsWith(item.href);
  return pathname === item.href;
}

interface QuotaData {
  available: number;
  total: number;
}

/**
 * Fetch the active company's quota for the persistent sidebar meter (J-C6:
 * quota visible in chrome on every company surface, not just /dashboard).
 * Returns null until loaded or on error so the meter simply doesn't render.
 */
function useCompanyQuota(
  companyId: string | undefined,
  active: boolean,
): QuotaData | null {
  const [quota, setQuota] = useState<QuotaData | null>(null);

  useEffect(() => {
    if (!active || !companyId) {
      setQuota(null);
      return;
    }
    let cancelled = false;
    apiFetch(`/api/companies/${companyId}/quota`, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) return;
        const body = (await res.json()) as { ok: boolean; data: QuotaData };
        if (!cancelled && body.ok) setQuota(body.data);
      })
      .catch(() => {
        /* sidebar meter is best-effort chrome; swallow + render nothing */
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, active]);

  return quota;
}

export function Sidebar({
  workspace,
  currentPathname,
  companyName,
  companyId = '',
  drawerOpen = false,
  onDrawerClose,
}: SidebarProps): ReactElement {
  const sidebarRef = useRef<HTMLElement>(null);
  const isCompany = workspace === 'company';
  const quota = useCompanyQuota(companyId, isCompany);

  let groups: NavGroup[];
  if (workspace === 'admin') {
    groups = ADMIN_GROUPS;
  } else if (workspace === 'company') {
    const g = companyGroups(companyId);
    // Replace generic "Company" label with the real company name if provided
    if (companyName && g[0]) g[0].label = companyName;
    groups = g;
  } else {
    groups = PERSONAL_GROUPS;
  }

  // Focus trap: when drawer opens on mobile, move focus into the sidebar.
  useEffect(() => {
    if (!drawerOpen) return;
    const firstFocusable = sidebarRef.current?.querySelector<HTMLElement>(
      'a[href], button, [tabindex="0"]',
    );
    firstFocusable?.focus();
  }, [drawerOpen]);

  return (
    <>
      {/* Scrim — mobile only, shown when drawer is open */}
      {drawerOpen && (
        <div
          className="app-drawer-scrim"
          aria-hidden="true"
          onClick={onDrawerClose}
          data-testid="drawer-scrim"
        />
      )}

      <aside
        ref={sidebarRef}
        id="app-sidebar"
        className={`app-side${drawerOpen ? ' drawer-open' : ''}`}
        aria-label="Workspace navigation"
        data-testid="sidebar"
        aria-hidden={drawerOpen === false ? undefined : undefined}
      >
        <div className="app-brand">
          <BrandMark />
          <b>BIMAssess</b>
        </div>

        {groups.map((group) => (
          <div key={group.label} className="app-navgrp" data-testid={`nav-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="app-navgrp-lbl">{group.label}</div>
            <nav className="app-nav">
              {group.items.map((item) => {
                const active = isActive(item, currentPathname);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`app-nav-link${active ? ' active' : ''}`}
                    aria-current={active ? 'page' : undefined}
                    data-testid="sidebar-link"
                    onClick={onDrawerClose}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        ))}

        {/* Persistent quota chrome (J-C6) — company mode only, pinned to the
            sidebar foot so it's visible on every company surface, not just
            /company/:id/dashboard. */}
        {isCompany && quota ? (
          <div className="app-side-foot" data-testid="sidebar-quota">
            <QuotaMeter
              quotaUsed={Math.max(0, quota.total - quota.available)}
              quotaTotal={quota.total}
            />
          </div>
        ) : null}
      </aside>
    </>
  );
}
